// https://github.com/brimdata/react-arborist
import { useEffect, useRef, useState } from 'react';
import { Tree } from 'react-arborist';
import styled from 'styled-components';
import { getElementById } from '../../../functions/launcherElementFunctions/getElementById';
import { moveElement } from '../../../functions/launcherElementFunctions/moveElement';
import { updateElementById } from '../../../functions/launcherElementFunctions/updateElementById';
import { updateObjectNestedProperties } from '../../../functions/objectFunctions/updateObjectNestedProperties';
import { useElements, useScene } from '../../../hooks';
import { useCursor } from '../../../hooks/useEditor';
import { useResizeObserver } from '../../../hooks/useResizeObserver';
import { CursorData } from '../../../types/hooks';
import { LauncherElement } from '../../../types/launcherElement';
import { Searchbar } from '../../common/Searchbar';
import { TextButton } from '../../generic/TextButton';
import { ElementTreeNode } from './ElementTreeNode';

export const ElementTree = () => {
  const treeRef = useRef();
  const scene = useScene();
  const [, setCursor] = useCursor<CursorData>();
  const [elements, setElements] = useElements<LauncherElement[]>();
  const [treeContainerRef, treeContainerSize] =
    useResizeObserver<HTMLDivElement>();

  const [searchQuery, setSearchQuery] = useState('');

  const onRename = ({ id, name }: any) => {
    let [element] = getElementById(elements, id);
    element = updateObjectNestedProperties(element, { name: name });
    const newElements = updateElementById(elements, id, element);
    if (!newElements) return;

    setElements(newElements);
  };

  const onMove = ({ dragIds, parentId, index }: any) => {
    const newElements = moveElement(elements, dragIds[0], parentId, index);
    if (!newElements) return;

    setElements(newElements);
    setCursor({
      isVisible: false,
    });
  };

  // Scroll to last selected Element
  useEffect(() => {
    const tree: any = treeRef.current!;
    if (!tree || scene.selectedElementIDs.length === 0) return;

    const lastSelectedElementID = scene.selectedElementIDs.slice(-1)[0];
    tree.scrollTo(lastSelectedElementID, { align: 'smart' });
  }, [scene.selectedElementIDs]);

  return (
    <>
      <Title>Element Tree</Title>
      <Searchbar searchQuery={searchQuery} setSearchQuery={setSearchQuery} />

      <IconContainer>
        <TextButton
          handleClick={() => {
            const tree: any = treeRef.current!;
            tree.closeAll();
          }}
        >
          Collapse all
        </TextButton>
      </IconContainer>

      <TreeContainer ref={treeContainerRef} id="elementTree">
        <Tree
          ref={treeRef}
          data={elements}
          searchTerm={searchQuery}
          onRename={onRename}
          onMove={onMove}
          childrenAccessor={(d: any) => d.props.children}
          openByDefault={false}
          width={'100%'}
          height={treeContainerSize?.height}
          indent={20}
          rowHeight={24}
          renderCursor={TreeCursor}
          onClick={() => {
            scene.setSelectedElementIDs([]);
          }}
        >
          {ElementTreeNode}
        </Tree>
      </TreeContainer>
    </>
  );
};

const Title = styled.h1`
  position: sticky;
  top: 0;
  z-index: 1;

  background-color: var(--panel-bg-color-secondary);
`;

const TreeContainer = styled.div`
  overflow: hidden;
  flex: 1;

  *:not(input):focus-visible {
    outline: none;
    border: none;

    &:before {
      content: '';
      box-sizing: border-box;
      position: absolute;
      top: 0px;
      left: 0px;
      width: 100%;
      height: 100%;
      border: 1px solid var(--accent-color);
      border-radius: 5px;
      z-index: 1;
    }
  }
`;

const TreeCursor = ({ top, left }: any) => {
  return (
    <div
      style={{
        pointerEvents: 'none',
        boxSizing: 'border-box',
        position: 'absolute',
        width: '100%',
        top: top,
        left: left,
        zIndex: 1,
        height: 1,
        borderBottom: '1px dashed var(--text-color)',
      }}
    />
  );
};

const IconContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-bottom: 5px;

  & > div {
    font-size: 11px;
    font-weight: 400;
    height: 22px;
  }
`;
